import { useCallback, useEffect, useState } from "react";
import { DollarSign, MapPin, ShieldAlert, TriangleAlert } from "lucide-react";

import { ApiError, apiGet } from "../api";
import { formatRecordedMoney, moneyField, projectionAsOf } from "../money";
import type { DeliveryCard, RiskData } from "../types";

function RiskRow({ d }: { d: DeliveryCard }) {
  const who = d.driver_name
    ? d.driver_name
    : d.courier_provider
      ? `${d.courier_provider} (courier)`
      : "unassigned";
  return (
    <div className="risk-row">
      <span className={`risk-score ${d.rto_risk_band === "high" ? "hi" : ""}`}>
        {d.rto_risk_score ?? "—"}
      </span>
      <div className="risk-main">
        <span className="risk-order">
          {d.order_name || "—"} · {d.customer_name || "Guest"}
        </span>
        <span className="risk-sub">
          {d.city && (
            <>
              <MapPin size={10} /> {d.city} ·{" "}
            </>
          )}
          {d.status} · <span className={d.driver_name || d.courier_provider ? "" : "unassigned"}>{who}</span>
        </span>
      </div>
      {d.payment_type === "cod" && <span className="chip chip-cod">Recorded COD {formatRecordedMoney(moneyField(d.recorded_money, "cod_amount"))}</span>}
    </div>
  );
}

export default function Risk() {
  const [data, setData] = useState<RiskData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await apiGet<RiskData>("/risk"));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load risk");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => void load(), 20000);
    return () => clearInterval(t);
  }, [load]);

  const c = data?.counts;
  const coverage = data?.coverage;
  const recorded = data?.recorded_cod?.contract_version === "console-recorded-money.v1"
    && data.recorded_cod.evidence_state === "legacy_recorded_unverified"
    && Array.isArray(data.recorded_cod.subtotals) ? data.recorded_cod : null;
  return (
    <div className="board-page">
      <div className="board-header">
        <h1>RTO &amp; COD risk</h1>
      </div>
      {err && <div className="banner-err">{err}{data ? " — showing the last successful read, which may be stale." : " — risk data is unavailable."}</div>}
      {data && (
        <p className="cap-note">
          {coverage ? (
            <>Read {coverage.returned_rows} ranked in-flight rows (cap {coverage.cap}); {projectionAsOf(coverage.as_of)}.
              {coverage.truncated ? " More rows are omitted; counts and subtotals are partial." : " Counts and subtotals cover this read selection."}</>
          ) : "Read coverage and as-of are unavailable; these rows are not a verified complete view."}
        </p>
      )}

      <div className="kpis">
        <div className="kpi kpi-risk">
          <div className="kpi-icon">
            <ShieldAlert size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">{c?.high ?? "—"}</div>
            <div className="kpi-label">High-risk rows (returned)</div>
          </div>
        </div>
        <div className="kpi kpi-attention">
          <div className="kpi-icon">
            <TriangleAlert size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">{c?.medium ?? "—"}</div>
            <div className="kpi-label">Medium-risk rows (returned)</div>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-icon">
            <DollarSign size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">{c?.cod_active ?? "—"}</div>
            <div className="kpi-label">Returned COD rows flagged not collected</div>
          </div>
        </div>
        <div className="kpi kpi-attention">
          <div className="kpi-icon">
            <DollarSign size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">
              {!recorded ? "Unknown" : recorded.subtotals.length > 0
                ? recorded.subtotals.map((subtotal) => (
                  <div key={subtotal.currency}>{formatRecordedMoney(subtotal)}</div>
                )) : recorded.observed_row_count === 0 ? "No COD rows in this selection" : "No known-currency subtotal"}
            </div>
            <div className="kpi-label">Recorded COD subtotals by currency (returned rows)</div>
            {recorded && <div className="kpi-label">{recorded.known_row_count} known; {recorded.unknown_row_count} unknown amount/currency rows excluded</div>}
          </div>
        </div>
      </div>
      <p className="cap-note">Recorded legacy fields only. The collection flag is unverified; these amounts do not prove collection, custody, settlement, a payable balance or bank cash.</p>

      <div className="risk-cols">
        <section className="lane lane-attention">
          <header className="lane-head">
            <span>High risk</span>
            <span className="lane-count">{data?.high.length ?? "—"}</span>
          </header>
          <div className="lane-body">
            {!data ? (
              <p className="lane-empty">{err ? "Risk data unavailable" : "Loading risk data…"}</p>
            ) : data.high.length === 0 ? (
              <p className="lane-empty">No high-risk deliveries in the returned rows</p>
            ) : (
              (data?.high ?? []).map((d) => <RiskRow key={d.id} d={d} />)
            )}
          </div>
        </section>
        <section className="lane lane-queue">
          <header className="lane-head">
            <span>Medium risk</span>
            <span className="lane-count">{data?.medium.length ?? "—"}</span>
          </header>
          <div className="lane-body">
            {!data ? (
              <p className="lane-empty">{err ? "Risk data unavailable" : "Loading risk data…"}</p>
            ) : data.medium.length === 0 ? (
              <p className="lane-empty">No medium-risk deliveries in the returned rows</p>
            ) : (
              (data?.medium ?? []).map((d) => <RiskRow key={d.id} d={d} />)
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
